import { CALLING_CODES, TIME_ZONE_COUNTRY } from './countries.generated';

/**
 * Country at sign-up, when the admin switch "country selection" is on. The server has the final
 * word (backend domain/country.ts); this only suggests a country and explains the currency.
 */

export const DEFAULT_COUNTRY = 'IN';

export const isCountry = (code: string | undefined | null): code is string => Boolean(code && CALLING_CODES[code]);

/** The country the device's time zone points at, then its language's region; undefined if neither helps. */
export const detectCountry = (
  timeZone: string | undefined = safeTimeZone(),
  languages: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language],
): string | undefined => {
  const fromZone = timeZone ? TIME_ZONE_COUNTRY[timeZone] : undefined;
  if (fromZone) return fromZone;
  for (const lang of languages) {
    const region = lang?.split('-')[1]?.toUpperCase();
    if (isCountry(region)) return region;
  }
  return undefined;
};

function safeTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** India pays in INR, Canada in CAD, the US and every other country in USD (mirrors the server). */
export const currencyFor = (country: string): 'INR' | 'CAD' | 'USD' =>
  country === 'IN' ? 'INR' : country === 'CA' ? 'CAD' : 'USD';

export const callingCodeOf = (country: string): string => CALLING_CODES[country] ?? '91';

const displayNames = (locale: string) => {
  try {
    return new Intl.DisplayNames([locale === 'hi' ? 'hi' : 'en'], { type: 'region' });
  } catch {
    return null;
  }
};

export const countryName = (code: string, locale = 'en'): string => displayNames(locale)?.of(code) ?? code;

/** Every country we can phone, by name in the parent's language, with India, the US and Canada first. */
export const countryOptions = (locale = 'en'): { code: string; name: string }[] => {
  const names = displayNames(locale);
  const all = Object.keys(CALLING_CODES).map((code) => ({ code, name: names?.of(code) ?? code }));
  const first = ['IN', 'US', 'CA'];
  return [
    ...first.map((code) => all.find((c) => c.code === code)!).filter(Boolean),
    ...all.filter((c) => !first.includes(c.code)).sort((a, b) => a.name.localeCompare(b.name, locale)),
  ];
};
